import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client/react";
import { Bell } from "lucide-react";

import { GET_NOTIFICATIONS } from "@/graphql/queries/notifications";
import { MARK_NOTIFICATIONS_READ } from "@/graphql/mutations/notifications";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/lib/auth/AuthContext";
import { formatRelativeDate } from "@/lib/relative-date";

/** Often enough that a letter earned mid-session is announced before the child
 *  leaves the page, rare enough to be free. Matches the AI status banner. */
const POLL_MS = 30_000;

type Notification = {
  id: string;
  kind: string;
  title: string;
  body: string | null;
  icon: string;
  link: string;
  readAt: string | null;
  createdAt: string;
};

/**
 * What is waiting for this learner, and where to go about it.
 *
 * Two things are announced here, and both were previously visible only in the
 * moment they happened: a letter (on the profile card, if you walked past it) and
 * an earned avatar (on the session finish screen, once). Neither waited for a
 * child who had closed the tab.
 *
 * Reading and acting are kept apart. Opening the bell marks everything seen —
 * being told is what the row promises — but the letter and the avatar are still
 * there to be collected, and the server takes its own row down only once the
 * thing itself is done.
 */
export function NotificationBell() {
  const { student } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const { data } = useQuery(GET_NOTIFICATIONS, {
    variables: { studentId: student?.id },
    skip: !student?.id,
    pollInterval: POLL_MS,
    fetchPolicy: "cache-and-network",
  });

  const [markRead] = useMutation(MARK_NOTIFICATIONS_READ);

  const feed = (data as any)?.notifications;
  const items: Notification[] = feed?.items ?? [];
  const unread: number = feed?.unreadCount ?? 0;

  // Nothing has ever happened for this learner — an empty bell to open is a
  // worse answer than no bell at all.
  if (!student?.id || items.length === 0) return null;

  const markAllRead = () => {
    if (unread === 0) return;
    markRead({ variables: { studentId: student.id } });
  };

  // Marked on the way out rather than the moment it opens: the reply updates the
  // list, and marking on open would strip the "these are the new ones" highlight
  // out from under a child who is still reading it.
  const openChanged = (next: boolean) => {
    setOpen(next);
    if (!next) markAllRead();
  };

  const go = (notification: Notification) => {
    // Closing by setting the controlled prop does not call onOpenChange, so this
    // path has to mark them itself.
    markAllRead();
    setOpen(false);
    navigate(notification.link);
  };

  return (
    <DropdownMenu open={open} onOpenChange={openChanged}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative text-muted-foreground/60 hover:text-muted-foreground"
          aria-label={unread > 0 ? `Pranešimai (${unread} nauji)` : "Pranešimai"}
          title="Pranešimai"
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-gradient-to-br from-pink-500 to-purple-500 px-1 text-[10px] font-bold text-white shadow-sm">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="border-b px-3 py-2 text-sm font-semibold">Pranešimai</div>

        <div className="max-h-96 overflow-y-auto">
          {items.map((notification) => (
            <button
              key={notification.id}
              onClick={() => go(notification)}
              className={`flex w-full items-start gap-3 border-b px-3 py-3 text-left transition-colors last:border-b-0 hover:bg-accent ${
                notification.readAt ? "" : "bg-primary/5"
              }`}
            >
              <span className="shrink-0 text-2xl leading-none">{notification.icon}</span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold leading-tight">
                  {notification.title}
                </span>
                {notification.body && (
                  <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                    {notification.body}
                  </span>
                )}
                <span className="mt-1 block text-[11px] text-muted-foreground">
                  {formatRelativeDate(notification.createdAt)}
                </span>
              </span>
              {/* An unread dot as well as the tint: the tint alone disappears on
                  a dark theme against a highlighted row. */}
              {!notification.readAt && (
                <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
              )}
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
